import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { anonymousRoles, identifiedRoles, ticketsPayload } from '@/helpers/BuyMembershipHelpers';
import { getMembershipConfig } from '@/helpers/Config';
import { environment } from '@/helpers/Environment';
import { getIdentityFormFields } from '@/helpers/IdentityFormHelpers';
import { csvToArray } from '@/helpers/StringHelpers';
import { reportFormValidity } from '@/helpers/Validation';
import MobileFooterPortal from '@/components/cart/MobileFooterPortal.vue';
import ReserveMobileFooter from '@/components/cart/ReserveMobileFooter.vue';
import BooleanRadios from '@/components/forms/BooleanRadios.vue';
import FormInput2 from '@/components/forms/FormInput2.vue';
import IdentityForm from '@/components/forms/IdentityForm.vue';
import Survey2 from '@/components/forms/Survey2.vue';
import MembershipLevel from '@/components/membership/MembershipLevel.vue';
import NamedMembers from '@/components/membership/NamedMembers.vue';
import UnnamedMembers from '@/components/membership/UnnamedMembers.vue';
import ValidationError from '@/errors/ValidationError';
import { languageItem } from '@/language/helpers';
@Component({
    name: 'MembershipForm',
    components: {
        IdentityForm,
        BooleanRadios,
        UnnamedMembers,
        NamedMembers,
        MembershipLevel,
        FormInput2,
        Survey2,
        MobileFooterPortal,
        ReserveMobileFooter,
    },
})
export default class default_1 extends Vue {
    @Prop({ required: true })
    event;
    @Prop({ required: true })
    ticketGroups;
    @Prop({ required: true })
    rules;
    @Prop({ default: () => new Set() })
    contexts;
    @Prop({ required: true })
    formId;
    @Prop()
    previousMembership;
    // Plain text error messages and content that may not be trusted.
    @Prop({ default: null })
    plainError;
    // Templated error messages that may include content like
    //    <membership-link>join now<membership-link> for free tickets.
    @Prop({ default: null })
    templateError;
    @Prop()
    selectedLevel;
    @Prop({ default: false })
    showExtendMembership;
    @Prop()
    showAutoRenew;
    @Prop()
    gifting;
    @Prop({ default: false })
    submitting;
    @Prop({ required: true })
    reserveButtonLabels;
    @Prop()
    autoRenewalLabels;
    @Prop()
    membershipLevelsTitle;
    @Prop({ default: {} })
    details;
    @Prop({ default: {} })
    countsByTTID;
    t;
    extendMembership = false;
    surveyAnswers = {};
    giftee = {};
    autoRenew = false;
    get selectedGroup() {
        return this.ticketGroups.find((group) => group.id === this.selectedLevel);
    }
    get surveySpec() {
        return this.selectedGroup?.additional_info_spec;
    }
    get identifiedRoles() {
        return identifiedRoles(this.rules, this.selectedLevel);
    }
    get anonymousRoles() {
        return anonymousRoles(this.rules, this.selectedLevel);
    }
    // The 'caption' string has no default value, but can be specified in portal strings.
    get gifteeHeader() {
        return languageItem('membershipRoute').gifteeHeader;
    }
    get gifteeFields() {
        if (this.gifting) {
            const omit = csvToArray('first_name, last_name, password, password_confirm, newsletter');
            return getIdentityFormFields(this.giftee, this.identityFormConfigurations, { omit });
        }
        else {
            return [];
        }
    }
    get contextsArray() {
        return Array.from(this.contexts);
    }
    submit() {
        this.isSubmitting = true;
        this.$emit('update:plainError', null);
        this.$emit('update:templateError', null);
        this.validate()
            .then(() => {
            const tickets = ticketsPayload(this.rules, this.selectedLevel, this.countsByTTID, this.details);
            const autoRenew = this.autoRenewOption?.automatic === 'prompt' ? this.autoRenew : false;
            const payload = { tickets, autoRenew, extendedMembership: this.extendedMembershipOption };
            if (tickets.length > 0) {
                this.$emit('submit', payload);
            }
            else {
                this.$emit('update:plainError', 'At least one member is required to purchase a membership');
                return Promise.reject(new ValidationError());
            }
        })
            .catch((error) => {
            this.isSubmitting = false;
            // Ignore validation errors and leave the promise resolved.
            if (!error.validationError) {
                throw error;
            }
        });
    }
    set isSubmitting(value) {
        this.$emit('update:submitting', value);
    }
    get isSubmitting() {
        return this.submitting;
    }
    validate() {
        return reportFormValidity(this.$refs.membershipForm);
    }
    get identityFormConfigurations() {
        return [
            this.selectedGroup?.meta.extra_checkout_fields || '',
            this.selectedGroup?.meta.identity_form || '',
            this.event?.meta.identity_form || '',
        ];
    }
    get extendedMembershipOption() {
        if (this.showExtendMembership && this.selectedGroup) {
            const legacy = environment.web.extended_membership_option
                ? { ...environment.web.extended_membership_option, toggle: 'enabled' }
                : undefined;
            return getMembershipConfig('extended', this.selectedGroup, this.event, legacy);
        }
        return null;
    }
    get autoRenewOption() {
        if (this.showAutoRenew && !this.contexts.has('gift') && this.selectedGroup) {
            const legacy = environment.config.ask_to_autorenew
                ? { automatic: 'prompt' }
                : undefined;
            return getMembershipConfig('renewal', this.selectedGroup, this.event, legacy);
        }
        return null;
    }
    get membershipDetailsTitle() {
        return this.gifting ? this.t.gift.title : this.t.title;
    }
    get membershipDetailsCaption() {
        return this.gifting ? this.t.gift.caption : this.t.caption;
    }
    updateDetails(id, value) {
        this.$emit('update:details', {
            ...this.details,
            [id]: value,
        });
        const count = value.filter((v) => !v.disabled).length;
        this.updateCountsByTTID(id, count);
    }
    updateCountsByTTID(id, value) {
        this.$emit('update:countsByTTID', {
            ...this.countsByTTID,
            [id]: value,
        });
    }
    @Watch('selectedGroup')
    onselectedGroupChanged() {
        this.autoRenew = false;
    }
    @Watch('extendMembership')
    onExtendMembershipChanged() {
        this.$emit('extendMembership', this.extendMembership);
    }
    @Watch('surveyAnswers')
    onSurveyAnswersChanged() {
        this.$emit('surveyAnswers', this.surveyAnswers);
    }
    @Watch('giftee')
    onGifteeChanged() {
        this.$emit('giftee', this.giftee);
    }
}
